import React from "react";
import CustomModal from "@/components/UI/Modals/CustomModal";
import EmployeeSingleUpload from "./EmployeeSingleUpload";
import AddRoleModal from "./AddRoleModal";
import AddPermissionModal from "./AddPermissionModal";
import AssignedUsersModal from "./AssignUsersToRoleModal";
import AddNewUsersModal from "./AddNewUsersModal";

/**
 * Modal management component
 * Implements Single Responsibility Principle by handling all modal rendering logic
 */
const ModalManager = ({
  modals,
  modalData,
  isSubmitting,
  setIsSubmitting,
  onCloseAddEmployeeModal,
  onCloseBulkImportModal,
  onCloseAddRoleModal,
  onCloseImportRolesModal,
  onCloseAddPermissionModal,
  onCloseImportPermissionsModal,
  onCloseAssignUsersModal,
  onCloseAddNewUsersModal,
  onOpenAddNewUsers,
  onEmployeeSubmit,
  onRoleSubmit,
  onPermissionSubmit,
  onAssignUsers,
  onAddNewUsers,
  onFetchAssignedUsers,
  employeeFormRef,
  rawEmployeeData,
  allUsers = [],
  isLoadingUsers = false,
}) => {
  const {
    selectedEmployee,
    selectedOriginalEmployee,
    modalMode,
    selectedRole,
    roleModalMode,
    selectedPermission,
    permissionModalMode,
  } = modalData;

  // console.log("ModalManager - Modal states:", modals);
  // console.log("ModalManager - Selected role:", selectedRole);

  // Track assigned user IDs dynamically
  const [currentAssignedUserIds, setCurrentAssignedUserIds] = React.useState(
    []
  );

  // Initialize assigned user IDs when modal opens
  React.useEffect(() => {
    if (modals.assignUsers && selectedRole) {
      const initialIds =
        selectedRole?.assignedUsers?.map(
          (user) => user.id || user.employeeId
        ) || [];
      setCurrentAssignedUserIds(initialIds);
    }
  }, [modals.assignUsers, selectedRole]);

  // Get assigned user IDs for the selected role (now dynamic)
  const assignedUserIds = currentAssignedUserIds;

  // Store the callback ref for adding new users
  const addNewUsersCallbackRef = React.useRef(null);

  // Handle when new users are added from AddNewUsersModal
  const handleAddNewUsersComplete = async (userIds) => {
    try {
      // console.log("handleAddNewUsersComplete called with:", userIds);

      // Update the assigned user IDs list
      setCurrentAssignedUserIds((prev) => {
        const newIds = [...prev, ...userIds];
        // console.log("Updated assigned user IDs:", newIds);
        return newIds;
      });

      // If we have a callback from AssignedUsersModal, call it to update the list
      if (addNewUsersCallbackRef.current) {
        // console.log("Calling AssignedUsersModal callback to update local list");
        addNewUsersCallbackRef.current(userIds);
      }

      // Close the AddNewUsers modal
      onCloseAddNewUsersModal();
    } catch (error) {
      console.error("Error in handleAddNewUsersComplete:", error);
      throw error;
    }
  };

  // Callback to store the reference from AssignedUsersModal
  const handleSetAddNewUsersCallback = (callback) => {
    // console.log('Setting add new users callback');
    addNewUsersCallbackRef.current = callback;
  };

  // Callback to update assigned user IDs when users are deleted
  const handleAssignedUsersChanged = (userIds) => {
    // console.log("Assigned users changed, updating IDs:", userIds);
    setCurrentAssignedUserIds(userIds);
  };

  return (
    <>
      {/* Add Employee Modal */}
      <CustomModal
        isOpen={modals.addEmployee}
        onClose={onCloseAddEmployeeModal}
      
        title={
          modalMode === "edit"
            ? "Edit Staff"
            : modalMode === "view"
            ? "View Staff"
            : "Single Upload"
        }
        subtitle={
          modalMode === "edit"
            ? "Update the staff details below."
            : modalMode === "view"
            ? "View the staff details below."
            : "Kindly fill in the details below to setup a single user."
        }
        width={600}
        height="100vh"
        rightOffset={0}
        primaryActionText={
          modalMode === "edit"
            ? "Update →"
            : modalMode === "view"
            ? null
            : "Create →"
        }
        onPrimaryAction={() => {
          // Don't set loading here - let validation complete first
          // Loading will be set in handleEmployeeSubmit after validation passes
          if (employeeFormRef.current) {
            employeeFormRef.current.submit();
          } else {
            console.warn("Form ref is not available");
          }
        }}
        secondaryActionText="Cancel"
        onSecondaryAction={onCloseAddEmployeeModal}
        hideSecondaryAction={modalMode === "view"}
        primaryActionDisabled={
          isSubmitting || employeeFormRef.current?.isLoading
        }
        loading={isSubmitting || employeeFormRef.current?.isLoading}
        noActionButton={modalMode === "view"}

      >
        <EmployeeSingleUpload
          onCancel={onCloseAddEmployeeModal}
          onSubmit={onEmployeeSubmit}
          formRef={employeeFormRef}
          mode={modalMode}
          employeeData={selectedOriginalEmployee}
          onLoadingChange={setIsSubmitting}
        />
      </CustomModal>

      {/* Add/Edit/View Role Modal */}
      <AddRoleModal
        isOpen={modals.addRole}
        onClose={onCloseAddRoleModal}
        onSubmit={onRoleSubmit}
        loading={isSubmitting}
        mode={roleModalMode || "create"}
        roleData={selectedRole}
      />

      {/* Add Permission Modal */}
      <AddPermissionModal
        isOpen={modals.addPermission}
        onClose={onCloseAddPermissionModal}
        onSubmit={onPermissionSubmit}
        loading={isSubmitting}
        mode={permissionModalMode || "create"}
        permissionData={selectedPermission}
      />

      {/* Assign Users to Role Modal */}
      <AssignedUsersModal
        modalOpen={modals.assignUsers}
        handleClose={onCloseAssignUsersModal}
        role={selectedRole}
        setShowNewUsersModal={onOpenAddNewUsers}
        onSave={onAssignUsers}
        onFetchAssignedUsers={onFetchAssignedUsers}
        onAddNewUsersToList={handleSetAddNewUsersCallback}
        onAssignedUsersChanged={handleAssignedUsersChanged}
        allUsers={allUsers}
        rightOffset={0}
      />

      {/* Add New Users Modal (nested modal) */}
      <AddNewUsersModal
        isOpen={modals.addNewUsers}
        onClose={onCloseAddNewUsersModal}
        onSave={handleAddNewUsersComplete}
        allUsers={allUsers}
        assignedUserIds={assignedUserIds}
        isLoadingUsers={isLoadingUsers}
        rightOffset={350} // Offset to position next to AssignedUsersModal
      />
    </>
  );
};

export default ModalManager;